import {
  filterLayersFromSceneHelper,
  getProcessedLayers,
} from "../../helpers/editView.helpers";
import { IProjectModel } from "../../stores/project/projectModel";
import "./EditView.scss";
import { observer } from "mobx-react-lite";
import LayersView from "./LayersView";
import {
  LayersAndModsPerScene,
  UpdateVideoParams,
  EditVideoTypes,
  EditVideoExposedToPlatformType,
  EditVideoInteractiveType,
  EditVideoTextType,
  UpdateTextParams,
} from "../../types/EditVideoTypes";
import { useEffect, useMemo, useState } from "react";
import { AsyncOpState } from "../../types/enums/async-op-states";
import { useMst } from "../../stores/Root";
import React from "react";
import { isExposedToPlatformMod, isInteractiveMod, isTextLayer } from "./utils";
import SearchLayers from "./SearchLayers";
import { AudioOverlay } from "../WelcomeProjectsGallery/UseCaseGallery/AudioOverlay";

type Props = {
  project: IProjectModel;
  selectedScene: string;
  handlePanelCollapse: (keys: string[] | string) => void;
  activePanels: string[];
  setAllLayersAndMods: React.Dispatch<
    React.SetStateAction<LayersAndModsPerScene[]>
  >;
  allLayersAndMods: LayersAndModsPerScene[];
  setCurrentSearch: (searchString: string) => void;
  setSearchView: (searchView: boolean) => void;
  hasUnpublishedBrandingChanges: boolean;
  selectedMenuItem: string;
  setSelectedMenuItem: (selectedMenuItem: string) => void;
  setSelectedScene: (selectedScene: string) => void;
  overlayAudio: {
    url: string;
    loop: boolean;
    volume: number;
  } | null;
  handleOverlayAudioChange: (overlayAudio: any) => void;
};

const EditView: React.FC<Props> = observer((props) => {
  const {
    project,
    allLayersAndMods,
    selectedScene,
    handlePanelCollapse,
    activePanels,
    setAllLayersAndMods,
    setCurrentSearch,
    setSearchView,
    selectedMenuItem,
    setSelectedMenuItem,
    setSelectedScene,
    overlayAudio,
    handleOverlayAudioChange,
  } = props;
  const [hasInteractive, setHasInteractive] = useState(false);
  const [hasExposedToPlatform, setHasExposedToPlatform] = useState(false);
  const { searchView, currentSearch } = useMst((store) => {
    const { editVideoStore } = store;
    return {
      hasUnsavedChanges:
        store.projectsStore.selectedProject?.hasUnpublishedConnnectorChanges,
      searchView: editVideoStore.searchView,
      currentSearch: editVideoStore.currentSearch,
      setSearchView: editVideoStore.setSearchView,
      setCurrentSearch: editVideoStore.setCurrentSearch,
    };
  });
  // Current input of the search box
  const currentScene = project.workspaceVideoParts?.find(
    (vp) => vp.name === selectedScene
  );

  const {
    allDynamicTextLayers: currentSceneDynamicTextLayers,
    allStaticTextLayers: currentSceneStaticTextLayers,
    allInteractiveMods: currentSceneInteractiveLayers,
    allExposedToPlatformConnectors: currentSceneExposedToPlatformConnectors,
  } = filterLayersFromSceneHelper(
    allLayersAndMods,
    currentScene?.name || "",
    currentSearch
  );

  const activePanel = useMemo(
    () => (activePanels[0] ? JSON.parse(activePanels[0]) : null),
    [activePanels]
  );

  useEffect(() => {
    if (activePanel?.sceneName !== selectedScene) {
      setSelectedScene(activePanel?.sceneName);
    }
  }, [activePanel, selectedScene, setSelectedScene]);
  // Saves the local state to db
  const save = async (sceneName: string, layerOrMod: EditVideoTypes) => {
    await project.saveMod(sceneName, layerOrMod);
    update();
    // after 3 second, reset panel save status on update
    setTimeout(() => {
      project.updateSaveModsStatus(AsyncOpState.Changed);
    }, 3000);
  };
  /**
   * This method will update the allLayers state to get the most up to date layers
   */
  const update = () => {
    let newState: LayersAndModsPerScene[] = [];
    project.workspaceVideoParts?.forEach((scene) => {
      const {
        allDynamicTextLayers,
        allStaticTextLayers,
        allInteractiveLayers,
        allExposedToPlatformConnectors,
      } = getProcessedLayers(scene, project);
      const currentSceneLayers: LayersAndModsPerScene = {
        scene: scene.name,
        allStaticTextLayers,
        allDynamicTextLayers,
        allInteractiveMods: allInteractiveLayers,
        allExposedToPlatformConnectors,
      };
      newState = [...newState, currentSceneLayers];
    });
    setAllLayersAndMods(newState);
  };

  useEffect(() => {
    update();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, currentScene?.assetLayerPairs, currentScene, project.lastSubscriptionUpdate]);

  useEffect(() => {
    //show single scene
    if (!searchView && currentScene?.name) {
      setHasInteractive(!!currentSceneInteractiveLayers?.length);
      setHasExposedToPlatform(
        !!currentSceneExposedToPlatformConnectors?.length
      );
    } else {
      setHasInteractive(
        project?.workspaceVideoParts?.some((vdp) => {
          const { allInteractiveMods: sceneInteractiveLayers } =
            filterLayersFromSceneHelper(
              allLayersAndMods,
              vdp.name || "",
              currentSearch
            );
          return sceneInteractiveLayers.length;
        })
      );
      setHasExposedToPlatform(
        project?.workspaceVideoParts?.some((vdp) => {
          const {
            allExposedToPlatformConnectors: sceneExposedToPlatformConnectors,
          } = filterLayersFromSceneHelper(
            allLayersAndMods,
            vdp.name || "",
            currentSearch
          );
          return sceneExposedToPlatformConnectors.length;
        })
      );
    }
  }, [
    allLayersAndMods,
    currentScene?.name,
    currentSceneInteractiveLayers?.length,
    currentSceneExposedToPlatformConnectors?.length,
    currentSearch,
    project?.workspaceVideoParts,
    searchView,
  ]);

  // Updates the state to have the latest changes
  const updateState = (params: UpdateVideoParams, scene?: any) => {
    project.updateRefreshModsStatus(AsyncOpState.Saving);
    const { input, layerOrMod } = params;
    if (isInteractiveMod(params.layerOrMod)) {
      if (
        "newCtaName" in input ||
        "newValue" in input ||
        "experimentId" in input
      ) {
        currentScene?.updateInteractiveMod(
          layerOrMod as EditVideoInteractiveType,
          input
        );
      }
    } else if (isExposedToPlatformMod(params.layerOrMod)) {
      if ("newValue" in input || "experimentId" in input) {
        currentScene?.updateExposedToPlatformMod(
          layerOrMod as EditVideoExposedToPlatformType,
          input
        );
      }
    } else if (isTextLayer(params.layerOrMod)) {
      if (
        (params as UpdateTextParams).action === "disable" &&
        ((layerOrMod as EditVideoTextType)?.connectedToMods ?? []).length > 0
      ) {
        if (scene) {
          scene.disableTextMods(
            (layerOrMod as EditVideoTextType)?.connectedToMods ?? []
          );
        } else {
          currentScene?.disableTextMods(
            (layerOrMod as EditVideoTextType)?.connectedToMods ?? []
          );
        }
      } else {
        currentScene?.updateTextMods(layerOrMod as EditVideoTextType, input);
      }
    }
    // Get the latest textLayers
    update();
    project.updateRefreshModsStatus(AsyncOpState.Success);
  };

  const handleOverride = (
    layerOrMod: EditVideoTypes | null,
    panelKey: string
  ) => {
    if (!layerOrMod) return;
    handlePanelCollapse(panelKey);
    const sceneFromPanelKey = JSON.parse(panelKey).sceneName;
    const scene = project.workspaceVideoParts?.find(
      (vp) => vp.name === sceneFromPanelKey
    );
    updateState(
      {
        action: "disable",
        layerOrMod,
      } as UpdateVideoParams,
      scene
    );
  };

  const content = [
    <div className="EditView">
      <>
        {project?.workspaceVideoParts?.map((vdp) => {
          const {
            allDynamicTextLayers: sceneDynamicTextLayers,
            allStaticTextLayers: sceneStaticTextLayers,
          } = filterLayersFromSceneHelper(
            allLayersAndMods,
            vdp?.name || "",
            currentSearch
          );
          if (sceneStaticTextLayers.length || sceneDynamicTextLayers.length) {
            return (
              <div key={vdp.name} className="SceneContainer">
                {sceneStaticTextLayers.length ||
                sceneDynamicTextLayers.length ? (
                  <div className="SceneName">
                    <span style={{ color: "#8B8D97" }}> Scene:</span> {vdp.name}
                  </div>
                ) : (
                  ""
                )}
                {sceneStaticTextLayers.length ? (
                  <LayersView
                    sceneName={vdp.name}
                    editableTypes={sceneStaticTextLayers}
                    activePanels={activePanels}
                    editable={true}
                    update={updateState}
                    save={save}
                    handlePanelCollapse={handlePanelCollapse}
                  />
                ) : (
                  ""
                )}
                {sceneDynamicTextLayers.length ? (
                  <div className="DynamicText">
                    <LayersView
                      sceneName={vdp.name}
                      editableTypes={sceneDynamicTextLayers}
                      editable={false}
                      activePanels={activePanels}
                      update={updateState}
                      save={save}
                      handlePanelCollapse={handlePanelCollapse}
                      onOverride={handleOverride}
                    />
                  </div>
                ) : (
                  ""
                )}
              </div>
            );
          }
        })}
      </>
    </div>,
    <div className="EditView">
      <>
        {project?.workspaceVideoParts?.map((vdp) => {
          const { allInteractiveMods: sceneInteractiveLayers } =
            filterLayersFromSceneHelper(
              allLayersAndMods,
              vdp.name || "",
              currentSearch
            );
          if (sceneInteractiveLayers.length) {
            return (
              <div key={vdp.name} className="SceneContainer">
                {sceneInteractiveLayers.length ? (
                  <div className="SceneName">
                    <span style={{ color: "#8B8D97" }}> Scene:</span> {vdp.name}
                  </div>
                ) : (
                  ""
                )}
                {sceneInteractiveLayers?.length ? (
                  <>
                    <LayersView
                      sceneName={vdp.name}
                      activePanels={activePanels}
                      editableTypes={sceneInteractiveLayers}
                      editable={true}
                      update={updateState}
                      save={save}
                      handlePanelCollapse={handlePanelCollapse}
                    />
                  </>
                ) : (
                  ""
                )}
              </div>
            );
          }
        })}
      </>
    </div>,
    <div className="EditView">
      <AudioOverlay
        overlayAudio={overlayAudio || undefined}
        handleOverlayAudioChange={handleOverlayAudioChange}
      />
      <br />
      <>
        {project?.workspaceVideoParts?.map((vdp) => {
          const {
            allExposedToPlatformConnectors: sceneExposedToPlatformConnectors,
          } = filterLayersFromSceneHelper(
            allLayersAndMods,
            vdp.name || "",
            currentSearch
          );
          if (sceneExposedToPlatformConnectors.length) {
            return (
              <div key={vdp.name} className="SceneContainer">
                {sceneExposedToPlatformConnectors.length ? (
                  <div className="SceneName">
                    <span style={{ color: "#8B8D97" }}> Scene:</span> {vdp.name}
                  </div>
                ) : (
                  ""
                )}
                {sceneExposedToPlatformConnectors?.length ? (
                  <>
                    <LayersView
                      sceneName={vdp.name}
                      activePanels={activePanels}
                      editableTypes={sceneExposedToPlatformConnectors}
                      editable={true}
                      update={updateState}
                      save={save}
                      handlePanelCollapse={handlePanelCollapse}
                    />
                  </>
                ) : (
                  ""
                )}
              </div>
            );
          }
        })}
      </>
    </div>,
  ];

  const getContent = () => {
    switch (selectedMenuItem) {
      case "Text":
        return content[0];
      case "Interactives":
        return content[1];
      case "Media & Controls":
        return content[2];
      default:
        return content[0];
    }
  };

  return (
    <div className="LayersFromSceneView">
      <div className="search-wrapper">
        <SearchLayers
          projectId={project.id}
          scene={selectedScene}
          layersAndMods={allLayersAndMods}
          handlePanelCollapse={handlePanelCollapse}
          setSearchView={setSearchView}
          setCurrentSearch={setCurrentSearch}
          setSelectedMenuItem={setSelectedMenuItem}
          setSelectedScene={setSelectedScene}
        />
      </div>
      <>{getContent()}</>
    </div>
  );
});

export default EditView;
